import { NotificationType, type NotificationContext, type NotificationDefinition, type NotificationDefinitionFactory } from "./notification.model";

export const DEFAULT_HEADINGS: Record<NotificationType, string> = {
    [NotificationType.Success]: "NotifyMessages.DefaultSuccessHeading",
    [NotificationType.Error]: "NotifyMessages.DefaultErrorHeading",
    [NotificationType.Warning]: "NotifyMessages.DefaultWarningHeading",
    [NotificationType.Info]: "NotifyMessages.DefaultInfoHeading",
};

export const NOTIFICATION_CATALOG: Record<string, NotificationDefinitionFactory> = {
    successfullySaved: buildSimpleNotification(
        NotificationType.Success,
        "NotifyMessages.SuccessfullySavedHeading",
        "NotifyMessages.SuccessfullySaved",
        5000,
    ),
    CancelSaved: buildSimpleNotification(NotificationType.Error, "NotifyMessages.CancelSavedHeading", "NotifyMessages.CancelSaved"),
    defaultError: buildSimpleNotification(NotificationType.Error, "Error.Service.default", "Error.Service.default"),
    UpdateIFirmTaxprepError: buildSimpleNotification(
        NotificationType.Error,
        "NotifyMessages.DefaultIFirmTaxprepSaveErrorTitle",
        "NotifyMessages.DefaultIFirmTaxprepSaveErrorContent",
    ),
    UpdateTrackingCardError: buildSimpleNotification(
        NotificationType.Error,
        "NotifyMessages.DefaultCardSaveErrorTitle",
        "NotifyMessages.DefaultCardSaveErrorContent",
    ),
    UpdateTrackingWithIFirmCardError: () => ({
        type: NotificationType.Error,
        heading: "NotifyMessages.DefaultCardWithIFirmSaveErrorTitle",
        content: [
            { text: "NotifyMessages.DefaultCardWithIFirmSaveErrorContentBeforeLink" },
            {
                text: "NotifyMessages.DefaultCardWithIFirmSaveErrorContentLinkText",
                url: "NotifyMessages.DefaultCardWithIFirmSaveErrorContentLinkUrl",
            },
            { text: "NotifyMessages.DefaultCardWithIFirmSaveErrorContentAfterLink" },
        ],
        autoCloseTimeoutMs: null,
    }),
    UpdateContactsError: buildSimpleNotification(
        NotificationType.Error,
        "NotifyMessages.DefaultContactsSaveErrorTitle",
        "NotifyMessages.DefaultContactsSaveErrorContent",
    ),
    UpdateJobError: buildSimpleNotification(
        NotificationType.Error,
        "NotifyMessages.DefaultJobSaveErrorTitle",
        "NotifyMessages.DefaultJobSaveErrorContent",
    ),
    "409ErrorUniqueEmail": buildSimpleNotification(NotificationType.Error, "Error.UniqueFieldEmail", "Error.UniqueFieldEmail"),
    "429TooManyRequests": buildSimpleNotification(NotificationType.Error, "Error.Service.429", "Error.Service.429"),
    "500Error": buildSimpleNotification(NotificationType.Error, "Error.Service.500", "Error.Service.500"),
    csvExportSuccess: buildSimpleNotification(NotificationType.Success, "Export.CsvTitle", "Export.Success", 5000),
    csvExportError: buildSimpleNotification(NotificationType.Error, "Export.CsvTitle", "Export.Fail"),
    filterSaveError: buildSimpleNotification(
        NotificationType.Error,
        "NotifyMessages.CancelSavedHeading",
        "NotifyMessages.FavoritesFiltersSaveFailContent",
    ),
    dragDisabledTooManyColumns: buildSimpleNotification(
        NotificationType.Warning,
        "NotifyMessages.DragDisabledTitle",
        "NotifyMessages.DragDisabledTooManyColumnsContent",
    ),
    dragDisabledPermissions: buildSimpleNotification(
        NotificationType.Warning,
        "NotifyMessages.DragDisabledTitle",
        "NotifyMessages.DragDisabledPermissionsContent",
    ),
    dragDisabledJobsPermissions: () => ({
        type: NotificationType.Warning,
        heading: "NotifyMessages.DragDisabledTitle",
        content: { text: "NotifyMessages.DragDisabledJobsPermissionsContent", isHtml: true },
    }),
    hiddenCards: (context) => buildHiddenCardsNotification(context, "HiddenCards"),
    hiddenJobsCards: (context) => buildHiddenCardsNotification(context, "HiddenJobsCards"),
    deleteError: buildSimpleNotification(NotificationType.Error, "NotifyMessages.CancelSavedHeading", "AllView.Events.ErrorMsg"),
    externalDeleteError: buildExternalDeleteErrorNotification,
};

function buildExternalDeleteErrorNotification(context: NotificationContext): NotificationDefinition {
    const { failedCount, applicationType } = context;
    if (typeof failedCount !== "number" || typeof applicationType !== "string") {
        throw new Error("externalDeleteError requires failedCount and applicationType in its context.");
    }

    const isSingleDelete = failedCount <= 1;

    return {
        type: NotificationType.Error,
        heading: isSingleDelete ? "Error.ExternalCardDelete" : "Error.ExternalCardsDelete",
        content: {
            text: isSingleDelete ? "Error.ExternalCardDeleteError" : "Error.ExternalCardsDeleteError",
            textParams: isSingleDelete ? { applicationType } : { count: failedCount, applicationType },
        },
        autoCloseTimeoutMs: null,
    };
}

function buildHiddenCardsNotification(context: NotificationContext, section: "HiddenCards" | "HiddenJobsCards"): NotificationDefinition {
    const count = context.count;
    if (typeof count !== "number" || count <= 0) {
        throw new Error(`${section} requires a positive count in its context.`);
    }

    const singular = count === 1 ? "Singular" : "";

    return {
        type: NotificationType.Warning,
        heading: `Error.${section}.Title${singular}`,
        content: [
            { text: `Error.${section}.NumberOfCardsNotVisible`, textParams: { count }, isHtml: true },
            { text: "Error.HiddenCards.BeforLinkText" },
            { text: "Error.HiddenCards.LinkText", url: "Error.HiddenCards.QuestionsLink" },
            { text: `Error.${section}.AfterLinkText${singular}` },
        ],
    };
}

function buildSimpleNotification(
    type: NotificationType,
    heading: string,
    text: string,
    autoCloseTimeoutMs: number | null = null,
): NotificationDefinitionFactory {
    return () => ({
        type,
        heading,
        content: { text },
        autoCloseTimeoutMs,
    });
}
